import React, { Component } from 'react'
import styled from 'styled-components'
import { IconButton } from 'react-mdl'
import Button from '../../components/Button'
import UnitElement from '../components/UnitElement'
import UnitElementModal from './UnitElementModal'

const Container = styled.div``
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`
const UnitElementContainer = styled.div`
  display: flex;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.muted};
  border-radius: 0.5em;
  margin-bottom: 0.25em;
  padding: 0.5em;
`

class UnitElementPasteInput extends React.Component {
  state = {
    element: '',
  }
  handleChange = ({ target }) => {
    this.setState({ element: target.value })
  }
  // Parses HTML or text data from TGA.
  handlePaste = e => {
    e.preventDefault()
    const html = e.clipboardData.getData('text/html')
    const text = e.clipboardData.getData('text')
    const listItems = this.getListItems(html, text)
    this.props.onAdd(listItems)
  }
  // Capitalises first letter and adds a full stop if no punctuation.
  fixGrammar = string => {
    const [, number = '', text = ''] = string.split(/^(\d+\.\d+\s)*(.+)/)
    let capitalised = text.charAt(0).toUpperCase() + text.slice(1)
    if (number) {
      capitalised = number + capitalised
    }
    return /\W$/.test(capitalised) ? capitalised : `${capitalised}.`
  }
  // Handles parsing of unit elements out of HTML and text data.
  getListItems(html, text) {
    const parser = new DOMParser()
    const htmlDom = parser.parseFromString(html, 'text/html')
    // Checks if HTML contains lists, processing as text if none exist
    if (htmlDom.querySelectorAll('ul,ol').length === 0) {
      // Splits text on new lines, filtering out empty elements; constructs unit elements.
      return text
        .split(/\n+/)
        .filter(text => text.trim() !== '')
        .reduce((carry, item, currentIndex) => {
          if (currentIndex === 0) return carry.concat(item)
          const [, number, text] = carry[carry.length - 1].split(/^(\d+\.\d+)*/)
          /* If previous item starts with number.number but has no text after
           * concat the current item text to the previous element. This is for
           * handling TGA formatting elements in tables, eg in the following unit:
           * http://training.gov.au/Training/Details/TLILIC2001#
          */
          if (number && text.trim() === '') {
            carry[carry.length - 1] = `${carry[carry.length - 1]} ${item}`
            return carry
          }
          // If the previous element is not a number.number with no text, concat the current item
          return carry.concat(item)
        }, [])
        .map(string => ({ text: this.fixGrammar(string) }))

      // return text
      //   .split(/\n+/)
      //   .filter(text => text.trim() !== '')
      //   .map(string => ({ text: this.fixGrammar(string) }))
    } else {
      /*
       * Manually handles nesting depth of TGA's invalid HTML 'lists'.
       * Go through each list and determines the nesting based on the left padding.
       */
      const depths = new Set()
      // Construct array of lists of format {depth: Number, items: [String]}
      const lists = [...htmlDom.querySelectorAll('ul,ol')].map(list => {
        const depth = parseInt(
          list.style.paddingLeft.slice(0, list.style.paddingLeft.length - 2),
          10,
        )
        depths.add(depth)
        const items = [...list.children].map(item => item.innerText)
        return { depth, items }
      })
      // Initialise unit elements and levels
      const unitElements = [],
        levels = Array.from(depths).sort()
      // Tracks last list of each nesting level
      let lastListOfLevel = {}
      for (const { depth, items } of lists) {
        const listItems = items.map(text => ({ text: this.fixGrammar(text) }))
        // Gets the last list of the current depth
        const lastList = lastListOfLevel[levels[levels.indexOf(depth) - 1]]
        // Adds sub-items to the appropriate element at the specified level list
        if (depth === levels[0]) {
          unitElements.push(...listItems)
        } else if (depth === levels[1]) {
          lastList.items = (lastList.items || []).concat(listItems)
        } else if (depth === levels[2]) {
          lastList.items = (lastList.items || []).concat(items)
        }
        // Sets the most recent list for the current depth
        lastListOfLevel[depth] = listItems.slice(-1)[0]
      }
      return unitElements
    }
  }
  render() {
    return (
      <input
        value="Paste elements here..."
        onPaste={this.handlePaste}
        readOnly
      />
    )
  }
}

function UnitElementEntry({ element, onMoveUp, onMoveDown, onDelete, onEdit }) {
  return (
    <UnitElementContainer>
      <div style={{ flexGrow: 1 }}>
        <UnitElement element={element} />
      </div>
      <div style={{ width: 64, flexShrink: 0 }}>
        <IconButton type="button" name="keyboard_arrow_up" onClick={onMoveUp} />
        <IconButton
          type="button"
          name="keyboard_arrow_down"
          onClick={onMoveDown}
        />
        <IconButton type="button" name="edit" onClick={onEdit} />
        <IconButton type="button" name="delete" onClick={onDelete} />
      </div>
    </UnitElementContainer>
  )
}

export default class UnitElementField extends Component {
  state = {
    editing: null,
  }
  handleAdd = elements =>
    this.props.onChange([...this.props.elements, ...elements])
  handleAddEmpty = () => this.handleAdd([{ text: '' }])
  handleChange = i => element =>
    this.props.onChange([
      ...this.props.elements.slice(0, i),
      element,
      ...this.props.elements.slice(i + 1),
    ])
  handleEdit = i => this.setState({ editing: i })
  handleDelete = i => () =>
    this.props.onChange([
      ...this.props.elements.slice(0, i),
      ...this.props.elements.slice(i + 1),
    ])
  handleMoveUp = i => () => {
    if (i === 0) return
    this.props.onChange([
      ...this.props.elements.slice(0, i - 1),
      this.props.elements[i],
      this.props.elements[i - 1],
      ...this.props.elements.slice(i + 1),
    ])
  }
  handleMoveDown = i => () => {
    if (i === this.props.elements.length - 1) return
    this.props.onChange([
      ...this.props.elements.slice(0, i),
      this.props.elements[i + 1],
      this.props.elements[i],
      ...this.props.elements.slice(i + 2),
    ])
  }
  render() {
    const { title, elements } = this.props
    const { editing } = this.state
    return (
      <Container>
        <Header>
          <h6 style={{ width: 200 }}>{title}</h6>
        </Header>
        {elements.map((element, i) =>
          <div key={i}>
            <UnitElementEntry
              element={element}
              onMoveUp={this.handleMoveUp(i)}
              onMoveDown={this.handleMoveDown(i)}
              onEdit={() => this.handleEdit(i)}
              onDelete={this.handleDelete(i)}
              onChange={this.handleChange(i)}
            />
          </div>,
        )}
        <Header>
          <span style={{ width: 200 }} />
          <UnitElementPasteInput onAdd={this.handleAdd} />
          <Button
            raised
            primary
            type="button"
            onClick={this.handleAddEmpty}>{`Add ${title}`}</Button>
        </Header>
        {editing != null &&
          <UnitElementModal
            element={elements[editing]}
            onChange={this.handleChange(editing)}
            onClose={this.handleEdit}
          />}
      </Container>
    )
  }
}



// WEBPACK FOOTER //
// ./src/Units/CreateUnit/UnitElementField.js